import React, { useCallback, useEffect, useState } from 'react';
import { Mail, RefreshCw, Send, X, Eye, Plus } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ReferralDigestCard({ token }) {
  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const [previewIdx, setPreviewIdx] = useState(-1);
  const [addSlug, setAddSlug] = useState('');
  const [testEmail, setTestEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [sentMsg, setSentMsg] = useState('');
  const headers = { Authorization: `Bearer ${token}` };
  const jsonHeaders = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/locator/digest`, { headers });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setData(await res.json());
      setLoadError(false);
    } catch (e) { console.error('digest fetch failed', e); setLoadError(true); }
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  const saveSlugs = async (slugs) => {
    const res = await fetch(`${API_URL}/api/admin/locator/digest/settings`, {
      method: 'PUT', headers: jsonHeaders, body: JSON.stringify({ enrolled_slugs: slugs }),
    });
    if (!res.ok) window.alert(`Save failed — ${(await res.json()).detail || 'try again'}`);
    setAddSlug('');
    load();
  };

  const sendNow = async () => {
    const target = testEmail.trim();
    const who = target ? `TEST address ${target}` : `the real facility contacts (${data.previews.map((p) => p.email).join(', ')})`;
    if (!window.confirm(`Send the month-to-date digest now to ${who}?`)) return;
    setSending(true);
    setSentMsg('');
    try {
      const res = await fetch(`${API_URL}/api/admin/locator/digest/send`, {
        method: 'POST', headers: jsonHeaders, body: JSON.stringify({ test_email: target || null }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || 'send failed');
      setSentMsg(`Sent to ${d.sent_to.join(', ')}`);
      load();
    } catch (e) { window.alert(`Send failed — ${e.message}`); } finally { setSending(false); }
  };

  if (loadError) return <div className="bg-white rounded-xl border border-red-100 p-4 mb-5 text-sm text-red-500" data-testid="digest-card-error">Referral digest failed to load. <button onClick={load} className="underline">Retry</button></div>;
  if (!data) return <div className="bg-white rounded-xl border border-gray-100 p-4 mb-5" data-testid="digest-card-loading"><RefreshCw className="w-4 h-4 text-gray-400 animate-spin" /></div>;

  const enrolled = data.facilities || [];
  const available = (data.claimed || []).filter((c) => !data.enrolled_slugs.includes(c.slug));
  const preview = previewIdx >= 0 ? data.previews[previewIdx] : null;

  return (
    <div className="bg-white rounded-xl border border-gray-100 p-4 mb-5" data-testid="referral-digest-card">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-1">
        <div>
          <h3 className="font-bold text-gray-900 text-sm flex items-center gap-2"><Mail className="w-4 h-4 text-emerald-600" /> Monthly Referral Digest</h3>
          <p className="text-xs text-gray-500 mt-1">Auto-sends on the 1st of each month (previous month&apos;s stats) to enrolled claimed facilities. Facilities sharing a contact get one combined email.</p>
        </div>
        <button onClick={load} className="text-xs text-gray-400 hover:text-gray-600 flex items-center gap-1" data-testid="digest-refresh-btn"><RefreshCw className="w-3 h-3" /> Refresh</button>
      </div>

      <div className="flex flex-wrap items-center gap-2 mt-3">
        {enrolled.map((f) => (
          <span key={f.slug} className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-emerald-50 border border-emerald-100 rounded-full text-xs text-emerald-800" data-testid={`digest-enrolled-${f.slug}`}>
            {f.name}
            <button onClick={() => saveSlugs(data.enrolled_slugs.filter((s) => s !== f.slug))} className="text-emerald-400 hover:text-red-500" aria-label={`Unenroll ${f.name}`} data-testid={`digest-remove-${f.slug}`}><X className="w-3 h-3" /></button>
          </span>
        ))}
        {enrolled.length === 0 && <span className="text-xs text-gray-400">No facilities enrolled.</span>}
        {available.length > 0 && (
          <span className="inline-flex items-center gap-1">
            <select value={addSlug} onChange={(e) => setAddSlug(e.target.value)} className="text-xs border border-gray-200 rounded-lg px-2 py-1 text-gray-600" data-testid="digest-add-select">
              <option value="">Add claimed facility…</option>
              {available.map((c) => <option key={c.slug} value={c.slug}>{c.name}{c.contact_email ? '' : ' (no email)'}</option>)}
            </select>
            {addSlug && <button onClick={() => saveSlugs([...data.enrolled_slugs, addSlug])} className="text-xs px-2 py-1 bg-emerald-600 text-white rounded-lg flex items-center gap-1" data-testid="digest-add-btn"><Plus className="w-3 h-3" /> Enroll</button>}
          </span>
        )}
      </div>

      <div className="mt-3 space-y-1.5">
        {data.previews.map((p, i) => (
          <div key={p.email} className="flex items-center justify-between gap-2 bg-gray-50 rounded-lg px-3 py-2" data-testid={`digest-group-${i}`}>
            <p className="text-xs text-gray-600 truncate">
              <strong className="text-gray-900">{p.contact_name}</strong> · {p.email} — {p.facility_names.join(' + ')}
              <span className="text-gray-400"> · {p.sections.reduce((s, x) => s + x.stats.profile_views, 0)} views / {p.sections.reduce((s, x) => s + x.stats.total_clicks, 0)} clicks {data.period_label ? 'MTD' : ''}</span>
            </p>
            <button onClick={() => setPreviewIdx(previewIdx === i ? -1 : i)} className="text-xs text-blue-600 hover:underline flex items-center gap-1 shrink-0" data-testid={`digest-preview-btn-${i}`}>
              <Eye className="w-3 h-3" /> {previewIdx === i ? 'Hide' : 'Preview'}
            </button>
          </div>
        ))}
        {data.previews.length === 0 && <p className="text-xs text-amber-600 bg-amber-50 rounded-lg px-3 py-2">No enrolled facility has a contact email — nothing will send.</p>}
      </div>

      {preview && (
        <div className="mt-3 border border-gray-200 rounded-lg overflow-hidden" data-testid="digest-preview-panel">
          <p className="text-xs text-gray-500 px-3 py-2 bg-gray-50 border-b border-gray-200">Subject: <strong className="text-gray-800">{preview.subject}</strong></p>
          <iframe title="digest preview" srcDoc={preview.html} sandbox="" className="w-full bg-black" style={{ height: 460, border: 0 }} data-testid="digest-preview-iframe" />
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2 mt-4 pt-3 border-t border-gray-100">
        <input value={testEmail} onChange={(e) => setTestEmail(e.target.value)} placeholder="Optional: test email (overrides recipients)"
          className="text-xs border border-gray-200 rounded-lg px-3 py-2 w-72 text-gray-700" data-testid="digest-test-email-input" />
        <button onClick={sendNow} disabled={sending || data.previews.length === 0}
          className="text-xs px-3 py-2 bg-emerald-600 text-white rounded-lg flex items-center gap-1.5 disabled:opacity-40 hover:bg-emerald-700"
          data-testid="digest-send-now-btn">
          <Send className="w-3 h-3" /> {sending ? 'Sending…' : 'Send month-to-date now'}
        </button>
        {sentMsg && <span className="text-xs text-emerald-600" data-testid="digest-sent-msg">{sentMsg}</span>}
      </div>

      {(data.log || []).length > 0 && (
        <div className="mt-3" data-testid="digest-log">
          <p className="text-[10px] uppercase tracking-wider text-gray-400 mb-1">Send history</p>
          {data.log.slice(0, 6).map((l, i) => (
            <p key={i} className="text-[11px] text-gray-500">{(l.sent_at || '').slice(0, 16).replace('T', ' ')} · {l.kind} · {l.period} → {l.email}{l.email !== l.intended_email ? ` (test, meant for ${l.intended_email})` : ''}</p>
          ))}
        </div>
      )}
    </div>
  );
}
